import React from "react";
import { Button } from "./ui/button";
import { ArrowRight, PhoneCall, BadgeCheck, Star } from "lucide-react";
import { company, heroStats } from "../mock";

export default function Hero() {
  return (
    <section id="home" className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-cyan-200/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-amber-200/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <h1 className="text-[26px] leading-[1.15] sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Is your online presence actually paying for itself
            <span className="block mt-2 text-cyan-700">
              or quietly flushing money
              <span className="block mt-1 lg:inline lg:mt-0 lg:ml-2">down the drain?</span>
            </span>
          </h1>

          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
            Your website, Google Business listing, social media and online reviews should be{" "}
            <em>earning</em> for you  not silently bleeding cash. Way too many small business owners
            and professionals have no clue which side they&rsquo;re actually on.{" "}
            <strong className="text-slate-900">Don&rsquo;t trust guesses; do a free, brutally honest performance check today!</strong>
          </p>

          <div className="mt-7 flex flex-col sm:flex-row flex-wrap sm:flex-nowrap items-stretch sm:items-center gap-2.5">
            <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white h-auto py-2 px-3.5 sm:px-4 lg:px-5 font-semibold w-full sm:w-auto">
              <a href="#contact" className="flex items-center justify-between gap-2 leading-tight">
                <span className="flex flex-col items-start text-left">
                  <span className="text-[11px] sm:text-[12px] lg:text-[13px] leading-snug whitespace-nowrap">FREE Website Audit &amp; Google Business Listing Analysis</span>
                  <span className="text-[10px] sm:text-[10px] lg:text-[11px] font-bold text-cyan-300 tracking-wide mt-0.5">($100 Value)</span>
                </span>
                <ArrowRight className="h-4 w-4 flex-shrink-0" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-10 sm:h-auto sm:py-2 px-4 sm:px-4 lg:px-5 text-[13px] sm:text-[13px] lg:text-sm font-semibold border-slate-300 text-slate-900 hover:bg-slate-100 w-full sm:w-auto whitespace-nowrap"
            >
              <a href={`tel:${company.phone}`}>
                <PhoneCall className="mr-2 h-4 w-4" /> {company.phone}
              </a>
            </Button>
          </div>

          {/* Signature: name + veteran badge */}
          <div className="mt-6 flex flex-col items-start gap-2">
            <div className="text-lg md:text-xl font-extrabold text-slate-900 tracking-tight">
              Eddie Kirk <span className="text-cyan-700">AzTn</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-xs font-semibold text-slate-700">
              <BadgeCheck className="h-3.5 w-3.5 text-cyan-600" />
              USMC Veteran Owned &middot; 20+ Years Experience
            </div>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex -space-x-2">
              {[
                "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&q=80",
                "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&q=80",
                "https://images.unsplash.com/photo-1629425733761-caae3b5f2e50?w=80&q=80",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="h-9 w-9 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
              <span className="ml-2 text-sm text-slate-600 font-medium">Trusted by local businesses</span>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80"
              alt="Digital marketing analytics"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex gap-3">
              <div className="flex-1 bg-white/95 backdrop-blur rounded-xl px-4 py-3 shadow-lg">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Live</div>
                <div className="text-sm font-bold text-slate-900">Google Visibility ↑ 184%</div>
              </div>
              <div className="flex-1 bg-white/95 backdrop-blur rounded-xl px-4 py-3 shadow-lg">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">This Month</div>
                <div className="text-sm font-bold text-slate-900">+47 New Leads</div>
              </div>
            </div>
          </div>

          <div className="absolute -bottom-6 -left-6 hidden md:block bg-white rounded-2xl border border-slate-200 shadow-xl p-4 w-56 animate-float">
            <div className="text-xs text-slate-500">Powered by</div>
            <div className="font-bold text-slate-900">AI &amp; NLP Tech</div>
            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full w-[88%] bg-cyan-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto mt-16 px-5 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {heroStats.map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
              <div className="text-2xl md:text-3xl font-extrabold text-slate-900">{s.value}</div>
              <div className="text-xs md:text-sm text-slate-500 font-medium mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
